<!-- Chat form presenting gender options as selectable buttons. -->

<script lang="ts" setup>
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';

import type { GenderInputConfig, GenderInputValue } from '@sigma/common/forms/GenderInput.js';
import { Gender, GenderTranslation } from '@sigma/common/models/Gender.js';

import Button from '../../Button.vue';

interface Props {
  config: GenderInputConfig;
}

interface Emits {
  submit: [value: GenderInputValue];
}

defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

function handleSubmit(gender: Gender) {
  return emitAsync('submit', { type: 'GENDER', gender });
}
</script>

<template>
  <Form
    v-slot="{ submit }"
    :class="css.optionsContainer"
  >
    <Button
      v-for="gender in Gender.options"
      :key="gender"
      :tid="'option-' + gender"
      align="left"
      :class="css.optionButton"
      :content-class="css.optionInner"
      @click="submit(() => handleSubmit(gender))"
    >
      {{ t(GenderTranslation, gender) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
.optionsContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.optionButton {
  padding: $spacing-12;
  border-radius: $border-radius-06;
  border: 1px solid $color-doctor-100;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out;

  &:hover {
    border-color: $color-doctor-200;
    background-color: $color-doctor-50;
  }

  &:focus-visible {
    outline: 2px solid $color-doctor-400;
    outline-offset: 2px;
  }
}

.optionInner {
  justify-content: flex-start;
}
</style>
